import type { CampaignType } from './campaign';
import type { SelectByType, WithExtraFields } from './utils';

export interface FetchPromoData {
  promotions: Array<Promotion>;
  winNotifications: Array<WinNotificationType>;
}

export interface FetchPromoConsentData {
  promotions: Array<Promotion>;
}

export interface FetchLeaderboardData {
  leaderboard: TournamentLeaderboardDetails;
  tournamentId: number;
}

export interface FetchLeaderboardAroundMeData {
  leaderboardAroundMe: TournamentLeaderboardAroundMeDetails;
  tournamentId: number;
}

export interface FetchPrizesAndWinnersData {
  prizes: PrizedropPrizeType[];
  winners: PrizeDropWinnersType[];
  prizeDropId: number;
  screenNameHidden: boolean;
}

export interface FetchCardsAndWinnersData {
  bonuscardId: number;
  cards: BonusCardType[];
  winners: BonusCardWinnersType[];
  screenNameHidden: boolean;
}

export interface FetchCashbackScoreData {
  score: CashbackScoreType | undefined;
  cahbackId: number;
}

interface NonBonusCardPromotionFields {
  roundsType: PromoRoundType | null;
}

export interface BasePromotion<T extends PromotionType = PromotionType> {
  campaignType: CampaignType.DefaultPromo;
  type: T;
  id: number;
  name: string;
  brandName: string;
  status?: string;
  clientMode?: string;
  opted: PromotionOptedStatus;
  gameIds: Array<string>;
  consentRequired: boolean;
  activeDuration: PromotionDuration;
  clientStyle?: string;
  isAsian: boolean;
  optJurisdiction?: string[];
  campaignDetails: {
    id: number | null;
    endDate: number | null;
  };
  ruleDetails: RuleDetails;
  prizeDetails: SelectByType<
    T,
    {
      [PromotionType.Tournament]: TournamentPrizeDetails;
      [PromotionType.Cashback]: CashbackPrizeDetails;
      [PromotionType.Prizedrop]: PrizedropPrizeDetails;
      [PromotionType.BonusCard]: BonusCardPrizeDetails;
    }
  >;
  userPrizeDetails: SelectByType<
    T,
    {
      [PromotionType.Tournament]: TournamentUserPrizeDetails;
      [PromotionType.Cashback]: CashbackUserScoreDetails;
      [PromotionType.Prizedrop]: PrizeDropWinnerUserPrizeDetails;
      [PromotionType.BonusCard]: BonusCardWinnerUserPrizeDetails;
    }
  >;
}

export type Promotion<T extends PromotionType = PromotionType> = T extends PromotionType.BonusCard
  ? BasePromotion<T>
  : BasePromotion<T> & NonBonusCardPromotionFields;

export interface PromotionDuration {
  fromEpoch: number;
  toEpoch: number;
}

export type TournamentPrizeList = Array<{
  fromRank: number;
  toRank: number;
  prizeDetails: PromotionPrize;
}>;
export interface TournamentPrizeDetails {
  minimumBetLimit: number;
  totalPrizeAmout: number;
  totalNoOfPrizes: number;
  highestPrize: string;
  prizes: Array<{
    fromRank: number;
    toRank: number;
    prizeDetails: PromotionPrize;
  }>;
  winCriterion: TournamentPromoWinCriterionType;
}

export interface CashbackPrizeDetails {
  minimumBetLimit: number;
  minCashbackAmount: number;
  maxCashbackAmount: number;
  type: CashbackPromoType;
}

export interface PrizedropPrizeDetails {
  minimumBetLimit: number;
  totalPrizeAmout: number;
  totalNoOfPrizes: number;
  highestPrize: string;
  prizes: PrizedropPrizeType[];
}

export interface BonusCardPrizeDetails {
  minimumBetLimit: number;
  totalPrizeAmount: number;
  totalNoOfCards: number;
  highestPrize: string;
  cards: BonusCardType[];
}

export interface TournamentUserPrizeDetails {
  leaderboard: TournamentLeaderboardDetails;
  leaderboard_aroundMe: TournamentLeaderboardAroundMeDetails;
}
export interface PrizeDropWinnerUserPrizeDetails {
  winners: PrizeDropWinnersType[];
  showWinnersList?: boolean;
  screenNameHidden: boolean;
}

export interface BonusCardWinnerUserPrizeDetails {
  winners: BonusCardWinnersType[];
  screenNameHidden: boolean;
}

export interface TournamentLeaderboardDetails {
  data: LeaderboardDataType[];
  screenNameHidden: boolean;
}

export interface TournamentLeaderboardAroundMeDetails {
  playerId: number | string;
  leaderboardItems: LeaderboardDataType[];
}

export interface CashbackUserScoreDetails {
  totalScore: number;
  cashbackScore: number;
  cashbackPercentage: number;
  roundLimitExceeded: boolean;
  displayScore: boolean;
}

export type PromotionPrize =
  | { type: PromotionPrizeType.Gift; gift: string }
  | { type: PromotionPrizeType.Coins; amount: number }
  | { type: PromotionPrizeType.Amount; amount: string }
  | { type: PromotionPrizeType.BetMultiplier; multiplier: number }
  | { type: PromotionPrizeType.FreeRounds; freeRoundType: string | SlotsFreeRoundType; amount?: number }
  | { type: PromotionPrizeType.Invalid };

export type PromotionBonusCard =
  | { type: BonusCardPromoType.FixedPrize; amount: number }
  | { type: BonusCardPromoType.MultiplierPrize; multiplier: number }
  | { type: BonusCardPromoType.Invalid };

export type LeaderboardDataType = {
  playerId: number;
  position: number;
  scoreDate: number;
  score: number;
  screenName: string;
};

export interface PrizedropPrizeType {
  id: number;
  size: number;
  originalSize: number;
  prizeDetails: PromotionPrize;
}

export interface BonusCardType {
  totalCount: number;
  remainingCount: number;
  cardDetails: PromotionBonusCard;
}

export type PrizeDropWinnersType = {
  amount: number | null;
  casinoUserId: string;
  gift: string | null;
  prizeId: number;
  screenName: string;
  winDate: number;
  multiplier?: number | null;
  freeRoundType?: SlotsFreeRoundType | null;
  freeRoundCount?: number | null;
};

export type BonusCardWinnersType = {
  amount: number | null;
  casinoUserId: string;
  screenName: string;
};

export interface CashbackScoreType {
  totalScore: number;
  cashbackScore: number;
  cashbackPercentage: number;
  roundLimitExceeded: boolean;
}

export interface RoundHistoryType {
  id: number;
  gameId: string;
  tableId: string;
  dateTime: number;
  bet: number;
  win: number;
}

export interface RoundHistoryByWSType {
  streakId: number;
  winstreakCount: number;
  winstreakPoints: number;
  lastPointsWinType: LastPointsWinType;
  rounds: RoundHistoryType[];
}

export interface WinNotificationType {
  promoType: PromotionType;
  promoId: number;
  name: string;
  brandName: string;
  isAsia: boolean;
  winType: PromotionPrizeType;
  prize: string;
  endDate: number;
  ruleDetails: RuleDetails;
  autoPay: boolean;
  minBetLimit: number;
  extraData: {
    autoPay?: boolean;
    type?: CashbackPromoType;
    multiplier?: number;
    bet?: number;
    minCashback?: number;
    maxCashback?: number;
    totalNoOfPrizes?: number;
    totalPrizeAmount?: number;
    highestPrize?: string;
    prizes?: PrizedropPrizeDetails['prizes'] | TournamentPrizeList;
    freeRoundType?: SlotsFreeRoundType | null;
    freeRoundAmount?: number | null;
  } | null;
}

//Socket response
export interface PrizeDropWinSocketResponse {
  amount: string;
  autoPay?: string;
  bet: string;
  id: string;
  mul: string;
  promoId: string;
  pdid?: string; // Fallback field when promoId is not available
  prizeType: PromotionPrizeType;
  seq: number;
  table: string;
  textPrize: string;
  transId: string;
  userId: string;
  wiid: string;
}

export interface DefaultDynamicFields {
  tournamentName: string;
  minBet: string | number;
  maxBet?: string | number;
  highestPrize?: string;
  ddwName?: string;
  totalPrizeAmount?: string | number;
  totalPrizeAmountBeautified?: string | number;
  highestPrizeBeautified?: string | number;
}

export interface HowToPlaySection {
  title: string;
  val: string;
}
export interface RuleDetails {
  howToPlay?: HowToPlaySection[];
  rule?: string;
  lang: string;
  currency: string;
  dynamic: WithExtraFields<DefaultDynamicFields>;
  ruleShort?: string;
}

//
// Enum definitions
//
export enum PromotionType {
  Tournament = 'TOURNAMENT',
  Cashback = 'CASHBACK',
  Prizedrop = 'PRIZEDROP',
  BonusCard = 'BONUSCARD',
}

export enum PromotionOptedStatus {
  Opted = 'opted',
  NonOpted = 'non-opted',
  Pop = 'pop',
  NoPop = 'no_pop',
  NA = 'NA',
}

export enum PromotionPrizeType {
  Gift = 'G',
  Coins = 'C',
  BetMultiplier = 'BM',
  FreeRounds = 'FR',
  Invalid = 'Invalid',
  Amount = 'A',
}

export enum TournamentPromoWinCriterionType {
  GenericPoints = 'GP',
  TotalPoints = 'TP',
  TotalBet = 'TB',
  WinStreak = 'WS',
}

export enum CashbackPromoType {
  Cashback = 'CASHBACK',
  Cashboost = 'CASHBOOST',
}

export enum BonusCardPromoType {
  FixedPrize = 'C',
  MultiplierPrize = 'BM',
  Invalid = 'Invalid',
}

export enum AsianRegions {
  Asia = 'Asia',
  Manila = 'Manila',
}

export enum DDWBrandName {
  DailyWins = 'Daily Wins',
  DropsAndWins = 'Drops & Wins',
}

export enum LastPointsWinType {
  CONSECUTIVE_LOSS = 'CONSECUTIVE_LOSS',
  CONSECUTIVE_WINS = 'CONSECUTIVE_WINS',
  CONSECUTIVE_WINS_MULTIPLIER = 'CONSECUTIVE_WINS_MULTIPLIER',
}

export enum PromoRoundType {
  Daily = 'DAILY',
  Total = 'TOTAL',
}

export enum SlotsFreeRoundType {
  FREE_BONUS_SPINS = 'N',
  SECONDS_OF_FREE_BONUS_SPINS = 'T',
  INSTANT_BONUS = 'F',
}
